import { Controller } from 'stimulus';

// Composer for formula fields (result measurements, and calculated task
// measurements). Renders a contenteditable area with inline chips for
// dynamic references (fields, traits, the row label) and plain text for
// literal math and functions. Serializes to the id-based token format
// consumed by FormulaTokens on the Rails side.
//
// A task measurement formula sees whole result columns (metadata
// column_scope), so a column must sit inside a function like AVERAGE.
export default class extends Controller {
  static targets = [
    'composer', 'hiddenInput', 'picker', 'functionPicker', 'metadata', 'preview',
    'pickerButton', 'pickerPanel', 'pickerSearch', 'pickerList',
  ];

  connect() {
    this.loadMetadata();
    this.deserialize(this.hiddenInputTarget.value || '');
    this.serialize();
    this.renderPreview();
    if (this.hasPickerListTarget) this.renderPickerList('');
  }

  disconnect() {
    this.closePicker();
  }

  loadMetadata() {
    const raw = this.metadataTarget.textContent || '{}';
    const parsed = JSON.parse(raw);
    this.parsed = parsed;
    this.namesByKey = { label: 'Label' };
    this.unitsByKey = {};
    this.columnScope = !!parsed.column_scope;
    this.functions = {};
    (parsed.functions || []).forEach((fn) => { this.functions[fn.name] = fn; });
    const groups = {
      field: parsed.fields || [],
      trait: parsed.traits || [],
      task_field: parsed.task_fields || [],
      task_parameter: parsed.task_parameters || [],
    };
    Object.entries(groups).forEach(([type, items]) => {
      items.forEach((item) => {
        this.namesByKey[`${type}:${item.id}`] = item.name;
        if (item.unit) this.unitsByKey[`${type}:${item.id}`] = item.unit;
      });
    });
  }

  deserialize(raw) {
    const composer = this.composerTarget;
    composer.innerHTML = '';
    if (!raw) return;

    const tokenRegex = /\{(field|trait|task_field|task_parameter|label)(?::(\d+))?\}/g;
    let lastIndex = 0;
    let match = tokenRegex.exec(raw);
    while (match !== null) {
      if (match.index > lastIndex) {
        composer.appendChild(document.createTextNode(raw.slice(lastIndex, match.index)));
      }
      composer.appendChild(this.buildChip(match[1], match[2]));
      lastIndex = tokenRegex.lastIndex;
      match = tokenRegex.exec(raw);
    }
    if (lastIndex < raw.length) {
      composer.appendChild(document.createTextNode(raw.slice(lastIndex)));
    }
  }

  paletteFor(type, missing) {
    if (missing) return 'bg-red-100 text-red-800 border-red-300';
    switch (type) {
      case 'field': return 'bg-blue-100 text-blue-800 border-blue-300';
      case 'trait': return 'bg-purple-100 text-purple-800 border-purple-300';
      case 'task_field': return 'bg-green-100 text-green-800 border-green-300';
      case 'task_parameter': return 'bg-amber-100 text-amber-800 border-amber-300';
      case 'label': return 'bg-gray-200 text-gray-800 border-gray-400';
      default: return 'bg-gray-100 text-gray-800 border-gray-300';
    }
  }

  previewColorFor(type) {
    switch (type) {
      case 'field': return 'text-blue-700';
      case 'trait': return 'text-purple-700';
      case 'task_field': return 'text-green-700';
      case 'task_parameter': return 'text-amber-700';
      default: return 'text-gray-700';
    }
  }

  buildChip(type, id) {
    const key = type === 'label' ? 'label' : `${type}:${id}`;
    const name = this.namesByKey[key];
    const missing = !name;

    const chip = document.createElement('span');
    chip.className = `formula-chip inline-block px-2 py-0.5 mx-0.5 rounded border text-xs font-medium align-middle ${this.paletteFor(type, missing)}`;
    chip.contentEditable = 'false';
    chip.dataset.token = type === 'label' ? '{label}' : `{${type}:${id}}`;
    chip.dataset.type = type;
    chip.dataset.id = id;
    chip.textContent = name || `${key}?`;
    return chip;
  }

  serialize() {
    let out = '';
    const walk = (node) => {
      if (node.nodeType === Node.TEXT_NODE) {
        out += node.textContent;
      } else if (node.nodeType === Node.ELEMENT_NODE) {
        if (node.dataset && node.dataset.token) {
          out += node.dataset.token;
        } else if (node.tagName === 'BR') {
          out += ' ';
        } else {
          node.childNodes.forEach(walk);
        }
      }
    };
    this.composerTarget.childNodes.forEach(walk);
    this.hiddenInputTarget.value = out.trim();
  }

  onInput() {
    this.serialize();
    this.renderPreview();
  }

  onPickerChange(event) {
    const select = event.target;
    const value = select.value;
    if (!value) return;

    const [type, id] = value.split(':');
    this.insertChip(type, id);
    select.value = '';
  }

  // Inserts a function name with its parentheses at the caret, leaving the
  // caret between them.
  onFunctionPick(event) {
    const select = event.target;
    const name = select.value;
    select.value = '';
    if (!name) return;

    const composer = this.composerTarget;
    composer.focus();
    this.restoreCaret();
    const text = document.createTextNode(`${name}()`);
    const selection = window.getSelection();
    if (selection.rangeCount > 0 && composer.contains(selection.anchorNode)) {
      const range = selection.getRangeAt(0);
      range.deleteContents();
      range.insertNode(text);
      range.setStart(text, name.length + 1);
      range.setEnd(text, name.length + 1);
      selection.removeAllRanges();
      selection.addRange(range);
    } else {
      composer.appendChild(text);
    }
    this.serialize();
    this.renderPreview();
  }

  insertChip(type, id) {
    const chip = this.buildChip(type, id);
    const trailing = document.createTextNode(' ');
    const composer = this.composerTarget;
    composer.focus();
    this.restoreCaret();

    const selection = window.getSelection();
    if (
      selection.rangeCount > 0 &&
      composer.contains(selection.anchorNode)
    ) {
      const range = selection.getRangeAt(0);
      range.deleteContents();
      range.insertNode(trailing);
      range.insertNode(chip);
      range.setStartAfter(trailing);
      range.setEndAfter(trailing);
      selection.removeAllRanges();
      selection.addRange(range);
    } else {
      composer.appendChild(chip);
      composer.appendChild(trailing);
    }

    this.serialize();
    this.renderPreview();
    this.rememberCaret();
  }

  // The caret's place in the formula, kept while the Insert list or the
  // function picker has focus, so an insert lands where the person was typing.
  rememberCaret() {
    const selection = window.getSelection();
    if (selection.rangeCount > 0 && this.composerTarget.contains(selection.anchorNode)) {
      this.savedRange = selection.getRangeAt(0).cloneRange();
    }
  }

  restoreCaret() {
    if (!this.savedRange || !this.composerTarget.contains(this.savedRange.startContainer)) return;

    const selection = window.getSelection();
    selection.removeAllRanges();
    selection.addRange(this.savedRange);
  }

  // ----- Insert list: what the formula can use, searchable -----

  pickerGroups() {
    const p = this.parsed || {};
    const columnScope = !!p.column_scope;
    const describe = (item, extra) => [extra, item.value, item.unit].filter(Boolean).join(' ');
    return [
      ['Row', [{ key: 'label', name: 'Label', meta: columnScope ? 'picks rows by label' : "the row's label" }]],
      [columnScope ? 'Result columns' : 'Result measurements', (p.fields || []).map((f) => ({ key: `field:${f.id}`, name: f.name, meta: describe(f, f.kind) }))],
      ['Task measurements', (p.task_fields || []).map((f) => ({ key: `task_field:${f.id}`, name: f.name, meta: describe(f) }))],
      ['Task parameters', (p.task_parameters || []).map((f) => ({ key: `task_parameter:${f.id}`, name: f.name, meta: describe(f) }))],
      ['Sample traits', (p.traits || []).map((f) => ({ key: `trait:${f.id}`, name: f.name, meta: describe(f) }))],
    ];
  }

  renderPickerList(query) {
    const q = query.trim().toLowerCase();
    const html = this.pickerGroups().map(([title, items]) => {
      const shown = items.filter((item) => !q || item.name.toLowerCase().includes(q));
      if (!shown.length) return '';
      return `<div class="me-pop__group">${this.escapeHtml(title)}</div>${shown.map((item) =>
        `<button type="button" role="option" class="me-pop__item" data-key="${this.escapeHtml(item.key)}" data-action="formula-composer#pickItem">` +
        `<span>${this.escapeHtml(item.name)}</span><span class="me-pop__meta">${this.escapeHtml(item.meta || '')}</span></button>`).join('')}`;
    }).join('');
    this.pickerListTarget.innerHTML = html || '<p class="me-pop__none">Nothing matches.</p>';
  }

  togglePicker() {
    if (this.pickerPanelTarget.hidden) this.openPicker();
    else this.closePicker();
  }

  openPicker() {
    this.rememberCaret();
    this.pickerSearchTarget.value = '';
    this.renderPickerList('');
    this.pickerPanelTarget.hidden = false;
    this.pickerButtonTarget.setAttribute('aria-expanded', 'true');

    // Open upward when the dialog has no room below the button.
    const body = this.element.closest('.me-body') || document.body;
    const below = body.getBoundingClientRect().bottom - this.pickerButtonTarget.getBoundingClientRect().bottom;
    const above = this.pickerButtonTarget.getBoundingClientRect().top - body.getBoundingClientRect().top;
    this.pickerPanelTarget.classList.toggle('me-pop--up', below < 320 && above > below);

    this.pickerSearchTarget.focus();
    this.outsideClick = (event) => {
      if (!this.pickerPanelTarget.contains(event.target) && !this.pickerButtonTarget.contains(event.target)) this.closePicker();
    };
    document.addEventListener('mousedown', this.outsideClick);
  }

  closePicker() {
    if (this.hasPickerPanelTarget) {
      this.pickerPanelTarget.hidden = true;
      this.pickerButtonTarget.setAttribute('aria-expanded', 'false');
    }
    if (this.outsideClick) document.removeEventListener('mousedown', this.outsideClick);
    this.outsideClick = null;
  }

  filterPicker() {
    this.renderPickerList(this.pickerSearchTarget.value);
  }

  // Escape closes; the arrow keys move between the search box and the items.
  pickerKey(event) {
    const items = Array.from(this.pickerListTarget.querySelectorAll('.me-pop__item'));
    const at = items.indexOf(document.activeElement);
    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      this.closePicker();
      this.pickerButtonTarget.focus();
    } else if (event.key === 'ArrowDown') {
      event.preventDefault();
      if (items.length) items[Math.min(at + 1, items.length - 1)].focus();
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      if (at <= 0) this.pickerSearchTarget.focus();
      else items[at - 1].focus();
    } else if (event.key === 'Enter' && document.activeElement === this.pickerSearchTarget) {
      // Never submit the editor from the search box.
      event.preventDefault();
      if (items.length) items[0].click();
    }
  }

  pickItem(event) {
    const key = event.currentTarget.dataset.key;
    this.closePicker();
    if (key === 'label') this.insertChip('label');
    else {
      const [type, id] = key.split(':');
      this.insertChip(type, id);
    }
  }

  renderPreview() {
    if (!this.hasPreviewTarget) return;

    const compact = this.data.get('compact') === 'true';
    const raw = this.hiddenInputTarget.value || '';
    if (!raw.trim()) {
      this.previewTarget.innerHTML = compact ? '' : '<span class="text-gray-400 italic">—</span>';
      return;
    }

    if (compact) {
      try {
        const tokens = this.tokenize(raw);
        const ast = this.parseExpression(tokens);
        if (tokens.pos < tokens.length) throw new Error('trailing input');
        this.checkColumns(ast);
        const derivedUnit = this.deriveUnit(ast);
        this.previewTarget.innerHTML = `<span class="me-check__ok">Reads as</span> ${this.renderNode(ast)}` +
          (derivedUnit ? ` <span class="me-check__unit">in ${this.escapeHtml(derivedUnit)}</span>` : '');
      } catch (err) {
        this.previewTarget.innerHTML = `<span class="me-check__err">Can't read this yet: ${this.escapeHtml(err.message)}</span>`;
      }
      return;
    }

    try {
      const tokens = this.tokenize(raw);
      const ast = this.parseExpression(tokens);
      if (tokens.pos < tokens.length) throw new Error('trailing input');
      this.checkColumns(ast);
      const mathHtml = this.renderNode(ast);
      const derivedUnit = this.deriveUnit(ast);
      const unitHtml = derivedUnit
        ? `<div class="mt-1 text-xs text-gray-500">Derived unit: <span class="font-medium text-gray-700">${this.escapeHtml(derivedUnit)}</span></div>`
        : '';
      this.previewTarget.innerHTML = mathHtml + unitHtml;
    } catch (err) {
      this.previewTarget.innerHTML = `<span class="text-red-500 italic">Can't preview: ${this.escapeHtml(err.message)}</span>`;
    }
  }

  // Same grammar as Formulas::Parser (app/services/formulas/parser.rb);
  // keep the two in step.
  tokenize(input) {
    const tokens = [];
    let i = 0;
    while (i < input.length) {
      const rest = input.slice(i);
      let m;
      if ((m = rest.match(/^\s+/))) {
        // skip
      } else if ((m = rest.match(/^\{label\}/i))) {
        tokens.push({ kind: 'label' });
      } else if ((m = rest.match(/^\{(field|trait|task_field|task_parameter):(\d+)\}/))) {
        tokens.push({ kind: 'ref', type: m[1], id: parseInt(m[2], 10) });
      } else if ((m = rest.match(/^\{[^}]*\}?/))) {
        throw new Error(`unknown reference ${m[0]}`);
      } else if ((m = rest.match(/^(\d+(\.\d*)?|\.\d+)([eE][-+]?\d+)?/))) {
        tokens.push({ kind: 'num', value: m[0] });
      } else if ((m = rest.match(/^"([^"]*)"/))) {
        tokens.push({ kind: 'str', value: m[1] });
      } else if (rest[0] === '"') {
        throw new Error('unclosed quote');
      } else if ((m = rest.match(/^[A-Za-z][A-Za-z0-9_.]*/))) {
        tokens.push({ kind: 'name', value: m[0].toUpperCase() });
      } else if ((m = rest.match(/^(<=|>=|<>|[-+*/^()<>=,])/))) {
        tokens.push({ kind: 'op', value: m[0] });
      } else {
        throw new Error(`unexpected character: ${rest[0]}`);
      }
      i += m[0].length;
    }
    tokens.pos = 0;
    return tokens;
  }

  peek(tokens) { return tokens[tokens.pos]; }
  consume(tokens) { return tokens[tokens.pos++]; }

  isOp(tokens, ...values) {
    const t = this.peek(tokens);
    return t && t.kind === 'op' && values.includes(t.value);
  }

  parseExpression(tokens) {
    const left = this.parseAdditive(tokens);
    if (this.isOp(tokens, '<', '<=', '>', '>=', '=', '<>')) {
      const op = this.consume(tokens).value;
      return { kind: 'binop', op, left, right: this.parseAdditive(tokens) };
    }
    return left;
  }

  parseAdditive(tokens) {
    let left = this.parseTerm(tokens);
    while (this.isOp(tokens, '+', '-')) {
      const op = this.consume(tokens).value;
      left = { kind: 'binop', op, left, right: this.parseTerm(tokens) };
    }
    return left;
  }

  parseTerm(tokens) {
    let left = this.parseUnary(tokens);
    while (this.isOp(tokens, '*', '/')) {
      const op = this.consume(tokens).value;
      left = { kind: 'binop', op, left, right: this.parseUnary(tokens) };
    }
    return left;
  }

  parseUnary(tokens) {
    if (this.isOp(tokens, '-', '+')) {
      const op = this.consume(tokens).value;
      const operand = this.parseUnary(tokens);
      return op === '-' ? { kind: 'unary', op: '-', operand } : operand;
    }
    return this.parsePower(tokens);
  }

  parsePower(tokens) {
    const base = this.parseBase(tokens);
    if (this.isOp(tokens, '^')) {
      this.consume(tokens);
      return { kind: 'binop', op: '^', left: base, right: this.parseUnary(tokens) };
    }
    return base;
  }

  parseBase(tokens) {
    const t = this.consume(tokens);
    if (!t) throw new Error('formula ends too soon');

    switch (t.kind) {
      case 'num': return { kind: 'num', value: t.value };
      case 'str': return { kind: 'str', value: t.value };
      case 'ref': return { kind: 'ref', type: t.type, id: t.id };
      case 'label': return { kind: 'label' };
      case 'name': return this.parseCall(t.value, tokens);
      default:
        if (t.value !== '(') throw new Error(`unexpected ${t.value}`);
        {
          const inner = this.parseExpression(tokens);
          const close = this.consume(tokens);
          if (!close || close.value !== ')') throw new Error('missing )');
          return { kind: 'group', inner };
        }
    }
  }

  parseCall(name, tokens) {
    const spec = this.functions[name];
    if (!spec) throw new Error(`unknown function ${name}`);
    if (!this.isOp(tokens, '(')) throw new Error(`${name} needs ( )`);
    this.consume(tokens);
    const args = [];
    if (!this.isOp(tokens, ')')) {
      args.push(this.parseExpression(tokens));
      while (this.isOp(tokens, ',')) {
        this.consume(tokens);
        args.push(this.parseExpression(tokens));
      }
    }
    const close = this.consume(tokens);
    if (!close || close.value !== ')') throw new Error('missing )');
    if (args.length < spec.min || (spec.max !== null && args.length > spec.max)) {
      throw new Error(`${name} takes ${spec.usage}`);
    }
    return { kind: 'call', name, args };
  }

  // A result column on its own is a list of values (task measurement
  // formulas); it has to sit inside a function such as AVERAGE or MAX.
  checkColumns(node, insideRangeFunction = false) {
    if (!node) return;
    switch (node.kind) {
      case 'ref':
      case 'label':
        if (this.columnScope && (node.kind === 'label' || node.type === 'field') && !insideRangeFunction) {
          throw new Error('a result column needs a function around it, like AVERAGE or MAX');
        }
        return;
      case 'group': this.checkColumns(node.inner, insideRangeFunction); return;
      case 'unary': this.checkColumns(node.operand, false); return;
      case 'binop':
        this.checkColumns(node.left, false);
        this.checkColumns(node.right, false);
        return;
      case 'call': {
        const range = this.functions[node.name] && this.functions[node.name].range;
        node.args.forEach((arg) => this.checkColumns(arg, range));
        return;
      }
      default:
    }
  }

  renderNode(node) {
    switch (node.kind) {
      case 'num':
        return this.escapeHtml(node.value);
      case 'str':
        return `<span class="text-gray-600">"${this.escapeHtml(node.value)}"</span>`;
      case 'label':
        return '<span class="italic font-medium text-gray-700">Label</span>';
      case 'call':
        return `<span><span class="font-semibold">${this.escapeHtml(node.name)}</span>(${node.args.map((a) => this.renderNode(a)).join(', ')})</span>`;
      case 'ref': {
        const key = `${node.type}:${node.id}`;
        const name = this.namesByKey[key];
        if (!name) return `<span class="italic text-red-600">${this.escapeHtml(key)}?</span>`;
        return `<span class="italic font-medium ${this.previewColorFor(node.type)}">${this.escapeHtml(name)}</span>`;
      }
      case 'group':
        return `<span>(${this.renderNode(node.inner)})</span>`;
      case 'unary':
        return `<span>−${this.renderNode(node.operand)}</span>`;
      case 'binop':
        return this.renderBinop(node);
      default:
        return '';
    }
  }

  renderBinop(node) {
    const L = this.renderNode(node.left);
    const R = this.renderNode(node.right);
    switch (node.op) {
      case '+':
        return `<span>${L} + ${R}</span>`;
      case '-':
        return `<span>${L} − ${R}</span>`;
      case '*':
        return `<span>${L} · ${R}</span>`;
      case '^':
        return `<span>${L}<sup style="font-size:0.75em;">${R}</sup></span>`;
      case '<=': return `<span>${L} ≤ ${R}</span>`;
      case '>=': return `<span>${L} ≥ ${R}</span>`;
      case '<>': return `<span>${L} ≠ ${R}</span>`;
      case '/':
        return (
          '<span style="display:inline-flex; flex-direction:column; vertical-align:middle; text-align:center; margin:0 0.25em;">' +
            `<span style="border-bottom:1px solid currentColor; padding:0 0.3em;">${L}</span>` +
            `<span style="padding:0 0.3em;">${R}</span>` +
          '</span>'
        );
      default:
        return `<span>${L} ${node.op} ${R}</span>`;
    }
  }

  deriveUnit(node) {
    switch (node.kind) {
      case 'num':
      case 'str':
      case 'label':
        return null; // dimensionless
      case 'call': {
        // Mirrors FormulaUnitDerive::UNIT_ARGS.
        const positions = {
          SUM: null, AVERAGE: null, MIN: null, MAX: null, MEDIAN: null, STDEV: null,
          ABS: [0], ROUND: [0], SUMIF: [2, 0], AVERAGEIF: [2, 0], MINIFS: [0], MAXIFS: [0], IF: [1, 2],
        };
        if (node.name === 'SQRT') {
          const inner = this.deriveUnit(node.args[0]);
          return inner ? `√(${inner})` : null;
        }
        if (!(node.name in positions)) return null;
        const indexes = positions[node.name] || node.args.map((_, i) => i);
        for (let k = 0; k < indexes.length; k += 1) {
          const arg = node.args[indexes[k]];
          const unit = arg ? this.deriveUnit(arg) : null;
          if (unit) return unit;
        }
        return null;
      }
      case 'ref': {
        const key = `${node.type}:${node.id}`;
        return this.unitsByKey[key] || null;
      }
      case 'group':
        return this.deriveUnit(node.inner);
      case 'unary':
        return this.deriveUnit(node.operand);
      case 'binop': {
        if (['<', '<=', '>', '>=', '=', '<>'].includes(node.op)) return null;
        const lu = this.deriveUnit(node.left);
        const ru = this.deriveUnit(node.right);
        switch (node.op) {
          case '+':
          case '-':
            // Same dimension — return whichever is defined
            return lu || ru;
          case '*':
            if (!lu) return ru;
            if (!ru) return lu;
            return `${lu}\u00b7${ru}`;
          case '/':
            if (!lu && !ru) return null;
            if (!ru) return lu;
            if (!lu) return `1/${ru}`;
            return `${lu}/${ru}`;
          case '^': {
            if (!lu) return null;
            const exp = this.exponentText(node.right);
            return exp ? `${lu}^${exp}` : lu;
          }
          default:
            return lu || ru;
        }
      }
      default:
        return null;
    }
  }

  exponentText(node) {
    if (node.kind === 'num') return node.value;
    if (node.kind === 'unary' && node.operand.kind === 'num') return `-${node.operand.value}`;
    return null;
  }

  escapeHtml(str) {
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }
}
